import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { createSelector } from 'reselect';

import ObservationItem from './ObservationItem';
import TrackItem from './TrackItem';
import actions from '../../../store/appActions';
import QueryBuilder from '../../../query-builder';
import { getTrackColor } from '../../../utils/trackColorUtil';
import { getPayloadAgentName } from '../../../utils/agentUtil';

import './Observation.scss';
const TRACKS_CONCEPT_URI =
  'https://sensingclues.poolparty.biz/SCCSSOntology/42';
const IMAGE_MEDIA_TYPES = new Set([
  'jpeg',
  'jpg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tiff',
  'heic',
  'heif'
]);
const isImageMediaType = mediaType => {
  const value = String(mediaType || '')
    .trim()
    .toLowerCase();
  if (!value) return false;
  return value.startsWith('image/') || IMAGE_MEDIA_TYPES.has(value);
};

const extractTagsFromConcepts = concepts => {
  const source = Array.isArray(concepts) ? concepts : [];
  const tags = source
    .map(concept => {
      if (!concept) return '';
      if (typeof concept === 'string') return concept;
      return concept.label || concept.name || concept.value || '';
    })
    .map(value => String(value || '').trim())
    .filter(Boolean);
  return tags;
};

const mergeObservationDetailWithListItem = (detailItem, listItem) => {
  if (!detailItem) return listItem;
  if (!listItem) return detailItem;

  const merged = {
    ...listItem,
    ...detailItem
  };

  if (
    (!Array.isArray(merged.concepts) || merged.concepts.length === 0) &&
    Array.isArray(listItem.concepts) &&
    listItem.concepts.length > 0
  ) {
    merged.concepts = listItem.concepts;
  }

  if (!Array.isArray(merged.tags) || merged.tags.length === 0) {
    if (Array.isArray(listItem.tags) && listItem.tags.length > 0) {
      merged.tags = listItem.tags;
    } else {
      const mergedConceptTags = extractTagsFromConcepts(merged.concepts);
      const listConceptTags = extractTagsFromConcepts(listItem.concepts);
      const detailConceptTags = extractTagsFromConcepts(
        detailItem && detailItem.concepts
      );
      const fallbackTags =
        mergedConceptTags.length > 0
          ? mergedConceptTags
          : listConceptTags.length > 0
          ? listConceptTags
          : detailConceptTags;
      if (fallbackTags.length > 0) {
        merged.tags = fallbackTags;
      }
    }
  }

  const mergedAgentName = getPayloadAgentName(merged);
  const listAgentName = getPayloadAgentName(listItem);
  if (!mergedAgentName && listAgentName) {
    merged.agent = {
      ...(listItem.agent || {}),
      ...(merged.agent || {}),
      agentName: listAgentName
    };
    if (!merged.agentName) merged.agentName = listAgentName;
    if (!merged.AgentName) merged.AgentName = listAgentName;
  }

  return merged;
};

class ObservationList extends Component {
  constructor(props) {
    super(props);
    const showPhotoResults =
      localStorage.getItem('resultsShowPhotoResults') === 'true';
    this.state = {
      scrollToTop: false,
      timelineLength: 0,
      showTrackResults: false,
      showAlertResults: false,
      showNoteResults: false,
      showPhotoResults,
      trackLoading: false,
      linkedObservationsLoadingTrackId: null,
      activeTrackListContext: null,
      focusedTrackAgent: null,
      preFocusResultToggles: null,
      hasZoomedFocusedTracks: false,
      focusedTrackLoadingActive: false,
      playbackTrackId: null,
      playbackRunning: false,
      playbackSpeedMultiplier: 1
    };

    this.ref = React.createRef();
    this.lastAutoFetchLength = -1;
    this.lastFocusedTrackTraceSyncLength = -1;
    this.focusedTrackTraceSyncInFlight = false;
    this.pendingTrackCountRequests = new Set();
    this.lastObservationModalRequestId = 0;
  }

  componentDidMount() {
    window.addEventListener('scroll', this.onScroll, true);
    window.addEventListener(
      'focus:show-all-tracks-for-agent',
      this.handleShowAllTracksForAgent
    );
    window.addEventListener(
      'focus:track-playback-status',
      this.handleTrackPlaybackStatus
    );
    const timelineItems = this.getTimelineItems(this.getResultsSource());
    const visibleItems = this.getVisibleItems(timelineItems);
    const displayItems = this.getDisplayItems(visibleItems);
    this.ensureVisibleTrackObservationCounts(displayItems);
    this.syncResultTypeTogglesFromSelectedConcepts();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.resetVersion !== this.props.resetVersion) {
      this.stopTrackPlayback();
      if (this.props.setTransientLoading) {
        this.props.setTransientLoading(false, '');
      }
      this.lastAutoFetchLength = -1;
      this.setState({
        showTrackResults: false,
        showAlertResults: false,
        showNoteResults: false,
        showPhotoResults: false,
        activeTrackListContext: null,
        focusedTrackAgent: null,
        preFocusResultToggles: null,
        hasZoomedFocusedTracks: false,
        focusedTrackLoadingActive: false,
        playbackTrackId: null,
        playbackRunning: false
      });
      localStorage.setItem('resultsShowTrackResults', 'false');
      localStorage.setItem('resultsShowAlertResults', 'false');
      localStorage.setItem('resultsShowNoteResults', 'false');
      localStorage.setItem('resultsShowPhotoResults', 'false');
      this.lastFocusedTrackTraceSyncLength = -1;
      this.focusedTrackTraceSyncInFlight = false;
    }

    if (prevProps.scrollToTop === false && this.props.scrollToTop === true) {
      this.ref.current.scrollTo({
        left: 0,
        top: 0
      });
    }

    if (prevProps.timelineResults !== this.props.timelineResults) {
      this.setState({ timelineLength: this.props.timelineResults.length });
      this.lastAutoFetchLength = -1;
    }

    if (
      prevProps.selectedConcepts !== this.props.selectedConcepts ||
      prevProps.ontology !== this.props.ontology
    ) {
      this.syncResultTypeTogglesFromSelectedConcepts();
    }

    const filtersChanged =
      prevProps.ontology !== this.props.ontology ||
      prevProps.timelineFetching !== this.props.timelineFetching ||
      prevProps.timelineTotal !== this.props.timelineTotal ||
      prevProps.timelineResults !== this.props.timelineResults ||
      prevProps.trackFeatures !== this.props.trackFeatures ||
      prevProps.selectedAgents !== this.props.selectedAgents ||
      prevState.showTrackResults !== this.state.showTrackResults ||
      prevState.showAlertResults !== this.state.showAlertResults ||
      prevState.showNoteResults !== this.state.showNoteResults ||
      prevState.showPhotoResults !== this.state.showPhotoResults;

    if (filtersChanged) {
      if (
        prevState.showTrackResults !== this.state.showTrackResults ||
        prevState.showAlertResults !== this.state.showAlertResults ||
        prevState.showNoteResults !== this.state.showNoteResults ||
        prevState.showPhotoResults !== this.state.showPhotoResults ||
        prevProps.trackFeatures !== this.props.trackFeatures
      ) {
        this.lastAutoFetchLength = -1;
      }
      const selectedIdentity = this.getSelectionIdentity(
        this.props.timelineSelectedItem
      );
      if (selectedIdentity) {
        const timelineItems = this.getTimelineItems(this.getResultsSource());
        const visibleItems = this.getVisibleItems(timelineItems);
        const visibleIdentities = new Set(
          (visibleItems || [])
            .map(item => this.getSelectionIdentity(item))
            .filter(Boolean)
        );
        const selectedTrackId = this.getSelectedTrackId(
          this.props.timelineSelectedItem
        );
        const isTrackSelection = Boolean(selectedTrackId);
        const selectedObservationId = this.getSelectedObservationId(
          this.props.timelineSelectedItem
        );
        if (!visibleIdentities.has(selectedIdentity)) {
          // Keep selected-track context stable for map-clicked track selection;
          // track details may arrive slightly later than selection state.
          if (isTrackSelection) {
            return;
          }
          // Keep selected observation stable when selected from map marker but
          // not yet present in the currently paged timeline results.
          if (selectedObservationId) {
            return;
          }
          if (this.state.activeTrackListContext) {
            this.setState({ activeTrackListContext: null });
          }
          this.props.clearSelectedItem();
          return;
        }
      }
      this.maybeFetchMoreFilteredResults();
    }

    if (
      prevProps.selected !== this.props.selected &&
      this.props.selected !== null
    ) {
      this.scrollSelectedItemIntoViewIfNeeded();
    }

    const prevObservationId = this.getSelectedObservationId(
      prevProps.timelineSelectedItem
    );
    if (
      prevObservationId &&
      !this.props.timelineSelectedItem &&
      prevProps.resetVersion === this.props.resetVersion
    ) {
      const el = document.getElementById(prevObservationId);
      if (el) {
        el.scrollIntoView({ block: 'center' });
      }
    }

    const selectedTrackId = this.getSelectedTrackId(
      this.props.timelineSelectedItem
    );
    const prevSelectedTrackId = this.getSelectedTrackId(
      prevProps.timelineSelectedItem
    );
    const selectedObservationId = this.getSelectedObservationId(
      this.props.timelineSelectedItem
    );
    if (
      !this.state.focusedTrackAgent &&
      !selectedTrackId &&
      selectedObservationId &&
      this.state.activeTrackListContext
    ) {
      if (!this.isObservationInActiveTrackContext(selectedObservationId)) {
        this.setState({ activeTrackListContext: null });
      }
    }
    if (
      !this.state.focusedTrackAgent &&
      !selectedTrackId &&
      !selectedObservationId &&
      this.state.activeTrackListContext
    ) {
      // Close-track and playback stop can dispatch selection clears
      // slightly out of order; clear stale track context whenever no
      // track/observation selection remains to keep results list visible.
      this.setState({ activeTrackListContext: null });
    }
    if (!this.state.focusedTrackAgent && selectedTrackId) {
      const activeTrackId =
        this.state.activeTrackListContext &&
        this.state.activeTrackListContext.trackId
          ? String(this.state.activeTrackListContext.trackId)
          : null;
      const shouldSyncSelectedTrackContext =
        String(selectedTrackId) !== String(prevSelectedTrackId) ||
        activeTrackId !== String(selectedTrackId);
      if (shouldSyncSelectedTrackContext) {
        this.setState({
          activeTrackListContext: {
            trackId: String(selectedTrackId),
            trackItem: this.resolveSelectedTrackContextItem(selectedTrackId),
            observations: this.getSelectedTrackObservationItems()
          }
        });
      }
    }

    if (
      prevProps.timelineResults !== this.props.timelineResults ||
      prevProps.timelineSelectedItem !== this.props.timelineSelectedItem ||
      prevProps.timelineSelectedTrackObservations !==
        this.props.timelineSelectedTrackObservations ||
      prevProps.trackObservationCounts !== this.props.trackObservationCounts ||
      prevState.showTrackResults !== this.state.showTrackResults ||
      prevState.showAlertResults !== this.state.showAlertResults ||
      prevState.showNoteResults !== this.state.showNoteResults ||
      prevState.showPhotoResults !== this.state.showPhotoResults
    ) {
      const timelineItems = this.getTimelineItems(this.getResultsSource());
      const visibleItems = this.getVisibleItems(timelineItems);
      const displayItems = this.getDisplayItems(visibleItems);
      this.ensureVisibleTrackObservationCounts(displayItems);
    }

    if (
      this.state.activeTrackListContext &&
      prevProps.timelineSelectedTrackObservations !==
        this.props.timelineSelectedTrackObservations
    ) {
      const selectedTrackObservationItems = this.getSelectedTrackObservationItems();
      // Keep the currently shown linked-observation list stable when a user
      // clicks a linked observation (redux clears selectedTrackObservations on
      // generic item selection). Only replace when we actually received items.
      if (selectedTrackObservationItems.length > 0) {
        this.setState(prevState => ({
          activeTrackListContext: prevState.activeTrackListContext
            ? {
                ...prevState.activeTrackListContext,
                observations: selectedTrackObservationItems
              }
            : null
        }));
      }
    }

    if (
      this.state.focusedTrackAgent &&
      !this.state.hasZoomedFocusedTracks &&
      (prevProps.timelineResults !== this.props.timelineResults ||
        prevState.focusedTrackAgent !== this.state.focusedTrackAgent)
    ) {
      const didZoom = this.tryZoomToFocusedTrackBounds();
      if (didZoom) {
        this.setState({ hasZoomedFocusedTracks: true });
      }
    }

    if (
      prevProps.selectedAgents !== this.props.selectedAgents &&
      this.state.focusedTrackAgent &&
      !this.isFocusedTrackAgentStillSelected()
    ) {
      this.setState({
        focusedTrackAgent: null,
        focusedTrackLoadingActive: false
      });
      if (this.props.setTransientLoading) {
        this.props.setTransientLoading(false, '');
      }
    }

    const previousLoadingLabel = String(
      (prevProps.dataLoading && prevProps.dataLoading.current) || ''
    )
      .trim()
      .toLowerCase();
    const isTrackScopedLoadingLabel =
      previousLoadingLabel === 'loading tracks...' ||
      previousLoadingLabel === 'loading track details...' ||
      previousLoadingLabel === 'loading all track traces...' ||
      previousLoadingLabel === 'loading track traces...' ||
      previousLoadingLabel === 'loading selected track traces...' ||
      previousLoadingLabel === 'zoom in on track bounds';
    if (
      prevProps.timelineFetching &&
      !this.props.timelineFetching &&
      isTrackScopedLoadingLabel &&
      !this.state.focusedTrackLoadingActive &&
      this.props.setTransientLoading
    ) {
      this.props.setTransientLoading(false, '');
    }

    if (this.state.focusedTrackAgent && this.state.focusedTrackLoadingActive) {
      const timelineItems = this.getTimelineItems(this.getResultsSource());
      const visibleItems = this.getVisibleItems(timelineItems);
      const expectedTrackCount = this.getFocusedTrackExpectedCount();
      const canLoadMoreRawResults =
        this.props.timelineResults.length < this.props.timelineTotal;
      const reachedExpectedTrackCount =
        expectedTrackCount > 0
          ? visibleItems.length >= expectedTrackCount
          : !canLoadMoreRawResults;
      const focusedTrackLoadingComplete =
        !this.props.timelineFetching &&
        (reachedExpectedTrackCount || !canLoadMoreRawResults);

      if (focusedTrackLoadingComplete) {
        this.setState({ focusedTrackLoadingActive: false });
        if (this.props.setTransientLoading) {
          this.props.setTransientLoading(false, '');
        }
      }
    }

    if (
      this.state.focusedTrackAgent &&
      prevProps.timelineResults !== this.props.timelineResults &&
      !this.props.timelineFetching
    ) {
      this.syncFocusedTrackTraces();
    }
  }

  onScroll = _.throttle(() => {
    if (this.state.focusedTrackAgent) {
      return;
    }
    const timelineItems = this.getTimelineItems(this.getResultsSource());
    const visibleItems = this.getVisibleItems(timelineItems);
    const {
      canLoadMoreRawResults,
      needsMoreForActiveView
    } = this.getListPagingState(visibleItems);

    if (
      this.ref.current.scrollTop + this.ref.current.clientHeight >=
        this.ref.current.scrollHeight - 500 &&
      this.props.timelineFetching === false &&
      canLoadMoreRawResults &&
      needsMoreForActiveView
    ) {
      this.props.startTimelineFetch();
      this.props.getTimeline(true);
    }
  }, 100);

  handleLoadMoreClick = () => {
    if (this.state.focusedTrackAgent || this.props.timelineFetching) {
      return;
    }
    const timelineItems = this.getTimelineItems(this.getResultsSource());
    const visibleItems = this.getVisibleItems(timelineItems);
    const {
      canLoadMoreRawResults,
      needsMoreForActiveView
    } = this.getListPagingState(visibleItems);
    if (this.hasReachedFilteredLimit(visibleItems)) {
      return;
    }
    if (!canLoadMoreRawResults || !needsMoreForActiveView) {
      return;
    }
    const { showTrackResults } = this.state;
    if (showTrackResults && this.props.setTransientLoading) {
      this.props.setTransientLoading(true, 'Loading track details...');
    }
    this.props.startTimelineFetch();
    this.props.getTimeline(true);
  };

  componentWillUnmount() {
    this.stopTrackPlayback(false);
    window.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener(
      'focus:show-all-tracks-for-agent',
      this.handleShowAllTracksForAgent
    );
    window.removeEventListener(
      'focus:track-playback-status',
      this.handleTrackPlaybackStatus
    );
  }

  handleShowAllTracksForAgent = event => {
    const detail = (event && event.detail) || {};
    const agentName = String(detail.agentName || '').trim();
    const matchValues = Array.isArray(detail.matchValues)
      ? detail.matchValues
      : [];
    const expectedTrackCount = Number(detail.expectedTrackCount) || 0;
    const focusContext =
      detail && detail.focusContext && typeof detail.focusContext === 'object'
        ? detail.focusContext
        : null;
    this.setState({
      showTrackResults: this.state.showTrackResults,
      showAlertResults: false,
      showNoteResults: false,
      showPhotoResults: false,
      activeTrackListContext: null,
      focusedTrackAgent: {
        agentName,
        matchValues,
        focusContext,
        expectedTrackCount
      },
      focusedTrackLoadingActive: true,
      hasZoomedFocusedTracks: false,
      preFocusResultToggles: this.state.focusedTrackAgent
        ? this.state.preFocusResultToggles
        : {
            showTrackResults: this.state.showTrackResults,
            showAlertResults: this.state.showAlertResults,
            showNoteResults: this.state.showNoteResults,
            showPhotoResults: this.state.showPhotoResults
          }
    });
    localStorage.setItem(
      'resultsShowTrackResults',
      String(this.state.showTrackResults)
    );
    localStorage.setItem('resultsShowAlertResults', 'false');
    localStorage.setItem('resultsShowNoteResults', 'false');
    localStorage.setItem('resultsShowPhotoResults', 'false');
    if (this.props.setTransientLoading) {
      this.props.setTransientLoading(true, 'Loading all track traces...');
    }
    this.lastFocusedTrackTraceSyncLength = -1;
    this.focusedTrackTraceSyncInFlight = false;
    this.props.clearSelectedItem();
  };

  syncFocusedTrackTraces = async () => {
    if (!this.state.focusedTrackAgent) return;
    if (!this.props.getTracksForCurrentFilters) return;
    if (this.focusedTrackTraceSyncInFlight) return;
    const timelineLength = Array.isArray(this.props.timelineResults)
      ? this.props.timelineResults.length
      : 0;
    if (timelineLength <= 0) return;
    if (timelineLength === this.lastFocusedTrackTraceSyncLength) return;

    this.focusedTrackTraceSyncInFlight = true;
    try {
      await this.props.getTracksForCurrentFilters();
      this.lastFocusedTrackTraceSyncLength = timelineLength;
    } finally {
      this.focusedTrackTraceSyncInFlight = false;
    }
  };

  normalizeDateValue = value =>
    value === null || value === undefined ? '' : String(value);

  getSelectedTrackId = item => {
    if (!item || typeof item !== 'object') return null;
    const props = item.properties || {};
    const entityType = String(
      props.EntityType || item.featureType || item.EntityType || ''
    )
      .trim()
      .toLowerCase();
    const isExplicitTrack = Boolean(
      item.featureId ||
        props.featureId ||
        (props.EntityType || '').toString().toLowerCase() === 'track' ||
        (props.EntityType || '')
          .toString()
          .toLowerCase()
          .indexOf('track/') === 0 ||
        entityType === 'track' ||
        entityType.indexOf('track/') === 0
    );
    if (!isExplicitTrack) return null;
    const trackId =
      item.featureId ||
      item.trackId ||
      item.EntityId ||
      props.EntityId ||
      props.featureId ||
      props.trackId ||
      props.TrackId;
    return trackId ? String(trackId) : null;
  };

  getSelectedObservationId = item => {
    if (!item || typeof item !== 'object') return null;
    const props = item.properties || {};
    const observationId =
      item.observationId ||
      item.ObservationId ||
      item.EntityId ||
      props.observationId ||
      props.ObservationId ||
      props.EntityId;
    return observationId ? String(observationId) : null;
  };

  isObservationItem = item => Boolean(this.getSelectedObservationId(item));

  getSelectionIdentity = item => {
    const trackId = this.getSelectedTrackId(item);
    if (trackId) return `track:${trackId}`;
    const observationId = this.getSelectedObservationId(item);
    if (observationId) return `obs:${observationId}`;
    return '';
  };

  isObservationInActiveTrackContext = observationId => {
    if (!observationId || !this.state.activeTrackListContext) return false;
    const contextObservations = Array.isArray(
      this.state.activeTrackListContext.observations
    )
      ? this.state.activeTrackListContext.observations
      : [];
    return contextObservations.some(observation => {
      const contextObservationId = this.getSelectedObservationId(observation);
      return (
        contextObservationId &&
        String(contextObservationId) === String(observationId)
      );
    });
  };

  getSelectedTrackObservationItems = () => {
    const selectedTrackObservations = Array.isArray(
      this.props.timelineSelectedTrackObservations
    )
      ? this.props.timelineSelectedTrackObservations
      : [];
    return selectedTrackObservations
      .map(entry => (entry && entry.Observation ? entry.Observation : entry))
      .filter(item => item && item.observationId)
      .sort(this.sortItemsByDateDesc);
  };

  resolveSelectedTrackContextItem = selectedTrackId => {
    const selectedItemTrackId = this.getSelectedTrackId(
      this.props.timelineSelectedItem
    );
    if (
      selectedItemTrackId &&
      String(selectedItemTrackId) === String(selectedTrackId)
    ) {
      return this.props.timelineSelectedItem;
    }
    const timelineItems = this.getTimelineItems(this.getResultsSource());
    const timelineMatch = timelineItems.find(item => {
      const itemTrackId = this.getSelectedTrackId(item);
      return itemTrackId && String(itemTrackId) === String(selectedTrackId);
    });
    if (timelineMatch) return timelineMatch;
    if (
      this.state.activeTrackListContext &&
      String(this.state.activeTrackListContext.trackId) ===
        String(selectedTrackId)
    ) {
      return this.state.activeTrackListContext.trackItem;
    }
    return null;
  };

  hasTrackFocusContextChanged = () => {
    const focused = this.state.focusedTrackAgent;
    const context = focused && focused.focusContext;
    if (!context) return true;
    return !(
      context.north === this.props.queryNorth &&
      context.east === this.props.queryEast &&
      context.south === this.props.querySouth &&
      context.west === this.props.queryWest &&
      this.normalizeDateValue(context.start) ===
        this.normalizeDateValue(this.props.queryStart) &&
      this.normalizeDateValue(context.end) ===
        this.normalizeDateValue(this.props.queryEnd)
    );
  };

  itemMatchesFocusedTrackAgentSelection = item => {
    const focused = this.state.focusedTrackAgent;
    if (!focused || !item) return false;
    const rawMatchValues = Array.isArray(focused.matchValues)
      ? focused.matchValues
      : [];
    const normalizedMatchValues = new Set(
      rawMatchValues
        .map(value => this.normalizeCompareValue(value))
        .filter(Boolean)
    );
    if (!normalizedMatchValues.size) return false;
    const candidates = [
      item && item.name,
      item && item.value,
      item && item.displayLabel,
      item && item.id,
      item && item.EntityId,
      item && item.agentId
    ];
    return candidates.some(candidate =>
      normalizedMatchValues.has(this.normalizeCompareValue(candidate))
    );
  };

  closeFocusedTracksView = async event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.state.focusedTrackAgent) return;
    this.stopTrackPlayback();
    if (this.props.setTransientLoading) {
      this.props.setTransientLoading(false, '');
    }
    const previousSelectedLayers =
      this.state.focusedTrackAgent &&
      this.state.focusedTrackAgent.focusContext &&
      Array.isArray(
        this.state.focusedTrackAgent.focusContext.selectedLayersBeforeFocus
      )
        ? this.state.focusedTrackAgent.focusContext.selectedLayersBeforeFocus
        : null;
    this.props.clearSelectedItem();

    const shouldFetchTimeline = this.hasTrackFocusContextChanged();
    await this.props.setSelectedAgents([]);

    const restoredToggles = {
      showTrackResults: false,
      showAlertResults: false,
      showNoteResults: false,
      showPhotoResults: false
    };
    this.setState({
      activeTrackListContext: null,
      focusedTrackAgent: null,
      preFocusResultToggles: null,
      focusedTrackLoadingActive: false,
      hasZoomedFocusedTracks: false,
      showTrackResults: restoredToggles.showTrackResults,
      showAlertResults: restoredToggles.showAlertResults,
      showNoteResults: restoredToggles.showNoteResults,
      showPhotoResults: restoredToggles.showPhotoResults
    });
    this.lastFocusedTrackTraceSyncLength = -1;
    this.focusedTrackTraceSyncInFlight = false;
    localStorage.setItem(
      'resultsShowTrackResults',
      String(restoredToggles.showTrackResults)
    );
    localStorage.setItem(
      'resultsShowAlertResults',
      String(restoredToggles.showAlertResults)
    );
    localStorage.setItem(
      'resultsShowNoteResults',
      String(restoredToggles.showNoteResults)
    );
    localStorage.setItem(
      'resultsShowPhotoResults',
      String(restoredToggles.showPhotoResults)
    );

    if (shouldFetchTimeline) {
      this.props.getTimeline(false);
    }
    if (previousSelectedLayers && this.props.setSelectedLayers) {
      await this.props.setSelectedLayers(previousSelectedLayers);
    }
    this.props.clearEntireTrack();
  };

  openObservationDetails = async item => {
    const observationId = this.getSelectedObservationId(item);
    if (!item || !observationId) {
      this.props.openObservationModal(item);
      return;
    }
    const requestId = this.lastObservationModalRequestId + 1;
    this.lastObservationModalRequestId = requestId;
    this.props.openObservationModal({
      ...item,
      isHydrating: true
    });
    if (!this.props.getSingleItem) return;
    let detailedItem = null;
    try {
      detailedItem = await this.props.getSingleItem(observationId);
    } catch (error) {
      if (requestId !== this.lastObservationModalRequestId) return;
      this.props.openObservationModal({
        ...item,
        isHydrating: false,
        hydrationError: true
      });
      return;
    }
    if (requestId !== this.lastObservationModalRequestId) return;
    this.props.openObservationModal({
      ...mergeObservationDetailWithListItem(detailedItem, item),
      isHydrating: false,
      hydrationError: false
    });
  };

  openObservationMedia = async item => {
    const observationId = this.getSelectedObservationId(item);
    if (!item || !observationId) return;
    this.props.openImageModal({ loading: true, images: [], index: 0 });
    let detailedItem = item;
    const currentMedia = Array.isArray(item.media) ? item.media : [];
    const hasImageMedia = currentMedia.some(
      mediaItem =>
        mediaItem &&
        mediaItem.Media &&
        isImageMediaType(mediaItem.Media.mediaType)
    );
    if (!hasImageMedia && this.props.getSingleItem) {
      const fetchedItem = await this.props.getSingleItem(observationId);
      if (fetchedItem) {
        detailedItem = fetchedItem;
      }
    }
    const media = Array.isArray(detailedItem.media) ? detailedItem.media : [];
    const imageMedia = media.filter(
      mediaItem =>
        mediaItem &&
        mediaItem.Media &&
        isImageMediaType(mediaItem.Media.mediaType)
    );
    if (!imageMedia.length) {
      this.props.openImageModal({ loading: false, images: [], index: 0 });
      return;
    }
    this.props.setSelectedItem(detailedItem);
    this.props.openImageModal({
      images: imageMedia,
      index: 0
    });
  };

  getTrackZoomCoordinates = () => {
    if (!this.state.focusedTrackAgent) return [];
    const timelineItems = this.getTimelineItems(this.getResultsSource());
    const focusedTrackItems = timelineItems.filter(
      item =>
        this.getItemType(item) === 'track' &&
        this.itemMatchesFocusedTrackAgent(item)
    );
    const coordinates = [];
    const pushCoord = coord => {
      if (!Array.isArray(coord) || coord.length < 2) return;
      const lng = Number(coord[0]);
      const lat = Number(coord[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      coordinates.push([lng, lat]);
    };
    const pushGeometry = geometry => {
      if (!geometry || !geometry.type || !geometry.coordinates) return;
      if (geometry.type === 'Point') {
        pushCoord(geometry.coordinates);
        return;
      }
      if (geometry.type === 'LineString') {
        geometry.coordinates.forEach(pushCoord);
        return;
      }
      if (geometry.type === 'MultiLineString') {
        geometry.coordinates.forEach(line => line.forEach(pushCoord));
      }
    };
    focusedTrackItems.forEach(item => {
      pushGeometry(item.geometry);
      if (Array.isArray(item.where) && item.where.length >= 2) {
        pushCoord(item.where);
      }
      if (item.startWhere && Array.isArray(item.startWhere.coordinates)) {
        pushCoord(item.startWhere.coordinates);
      }
      if (item.endWhere && Array.isArray(item.endWhere.coordinates)) {
        pushCoord(item.endWhere.coordinates);
      }
    });
    return coordinates;
  };

  tryZoomToFocusedTrackBounds = () => {
    const coordinates = this.getTrackZoomCoordinates();
    if (!coordinates.length) return false;
    window.dispatchEvent(
      new CustomEvent('focus:zoom-to-track-item-bounds', {
        detail: { coordinates }
      })
    );
    return true;
  };

  clearSelectedMapResultIfNeeded = () => {
    const selectedItem = this.props.timelineSelectedItem || {};
    const selectedEntityType = String(
      (selectedItem.properties && selectedItem.properties.EntityType) || ''
    ).toLowerCase();
    const hasSelectedObservation = Boolean(
      selectedItem.observationId ||
        selectedItem.ObservationId ||
        selectedItem.EntityId
    );
    const hasSelectedTrack = Boolean(
      selectedItem.featureId || selectedEntityType.indexOf('track') === 0
    );
    if (hasSelectedObservation || hasSelectedTrack) {
      this.props.clearSelectedItem();
    }
  };

  normalizeConceptToken = value =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

  getConceptNodeIdentity = node =>
    String(
      (node &&
        (node.id || node.uri || node.URI || node.key || node.value || '')) ||
        ''
    ).trim();

  getConceptNodeTokens = node =>
    [
      node && node.id,
      node && node.uri,
      node && node.URI,
      node && node.key,
      node && node.value,
      node && node.name,
      node && node.label
    ]
      .map(this.normalizeConceptToken)
      .filter(Boolean);

  findConceptNodeByLabel = (nodes, label) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;
    const normalizedLabel = String(label || '')
      .trim()
      .toLowerCase();
    if (!normalizedLabel) return null;
    for (let index = 0; index < nodes.length; index += 1) {
      const node = nodes[index];
      const nodeLabel = String((node && node.label) || '')
        .trim()
        .toLowerCase();
      if (
        nodeLabel === normalizedLabel ||
        nodeLabel.startsWith(`${normalizedLabel} (`)
      ) {
        return node;
      }
      const childHit = this.findConceptNodeByLabel(
        node && node.children,
        label
      );
      if (childHit) return childHit;
    }
    return null;
  };

  findConceptNodeByTokenHints = (nodes, hints) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;
    const normalizedHints = (Array.isArray(hints) ? hints : [])
      .map(this.normalizeConceptToken)
      .filter(Boolean);
    if (!normalizedHints.length) return null;

    for (let index = 0; index < nodes.length; index += 1) {
      const node = nodes[index];
      const tokens = this.getConceptNodeTokens(node);
      const isMatch = normalizedHints.some(hint =>
        tokens.some(token => token === hint || token.endsWith(hint))
      );
      if (isMatch) return node;
      const childHit = this.findConceptNodeByTokenHints(
        node && node.children,
        normalizedHints
      );
      if (childHit) return childHit;
    }
    return null;
  };

  getResultTypeConceptIds = () => {
    const ontology = Array.isArray(this.props.ontology)
      ? this.props.ontology
      : [];
    const informationTypeNode =
      this.findConceptNodeByLabel(ontology, 'Information type') ||
      this.findConceptNodeByTokenHints(ontology, ['informationtype']);
    const informationTypeChildren =
      informationTypeNode && Array.isArray(informationTypeNode.children)
        ? informationTypeNode.children
        : [];

    const trackNode =
      this.findConceptNodeByLabel(informationTypeChildren, 'Track') ||
      this.findConceptNodeByLabel(ontology, 'Tracks') ||
      this.findConceptNodeByLabel(ontology, 'Track') ||
      this.findConceptNodeByTokenHints(ontology, ['track']);
    const alertNode =
      this.findConceptNodeByLabel(informationTypeChildren, 'Alert') ||
      this.findConceptNodeByLabel(ontology, 'Alert') ||
      this.findConceptNodeByTokenHints(ontology, ['alert']);
    const noteNode =
      this.findConceptNodeByLabel(informationTypeChildren, 'Note') ||
      this.findConceptNodeByLabel(ontology, 'Note') ||
      this.findConceptNodeByTokenHints(ontology, ['note']);

    return {
      track: this.getConceptNodeIdentity(trackNode) || TRACKS_CONCEPT_URI,
      alert: this.getConceptNodeIdentity(alertNode),
      note: this.getConceptNodeIdentity(noteNode)
    };
  };

  getSelectedConceptIdSet = () =>
    new Set(
      (Array.isArray(this.props.selectedConcepts)
        ? this.props.selectedConcepts
        : []
      )
        .filter(item => typeof item === 'string')
        .map(item => String(item).trim())
        .filter(Boolean)
    );

  syncResultTypeTogglesFromSelectedConcepts = () => {
    const resultTypeConceptIds = this.getResultTypeConceptIds();
    const selectedConceptIds = this.getSelectedConceptIdSet();
    const nextShowTrackResults =
      Boolean(resultTypeConceptIds.track) &&
      selectedConceptIds.has(resultTypeConceptIds.track);
    const nextShowAlertResults =
      Boolean(resultTypeConceptIds.alert) &&
      selectedConceptIds.has(resultTypeConceptIds.alert);
    const nextShowNoteResults =
      Boolean(resultTypeConceptIds.note) &&
      selectedConceptIds.has(resultTypeConceptIds.note);

    if (
      nextShowTrackResults === this.state.showTrackResults &&
      nextShowAlertResults === this.state.showAlertResults &&
      nextShowNoteResults === this.state.showNoteResults
    ) {
      return;
    }

    this.setState({
      showTrackResults: nextShowTrackResults,
      showAlertResults: nextShowAlertResults,
      showNoteResults: nextShowNoteResults
    });
    localStorage.setItem(
      'resultsShowTrackResults',
      String(nextShowTrackResults)
    );
    localStorage.setItem(
      'resultsShowAlertResults',
      String(nextShowAlertResults)
    );
    localStorage.setItem('resultsShowNoteResults', String(nextShowNoteResults));
  };

  toggleResultType = type => {
    this.clearSelectedMapResultIfNeeded();
    this.stopTrackPlayback();
    if (this.state.focusedTrackAgent) {
      this.setState({ focusedTrackAgent: null });
    }
    if (type === 'tracks' || type === 'alerts' || type === 'notes') {
      const resultTypeConceptIds = this.getResultTypeConceptIds();
      const conceptIdByType = {
        tracks: resultTypeConceptIds.track,
        alerts: resultTypeConceptIds.alert,
        notes: resultTypeConceptIds.note
      };
      const targetConceptId = conceptIdByType[type];
      if (!targetConceptId || !this.props.setSelectedConcepts) {
        return;
      }

      const existingConcepts = Array.isArray(this.props.selectedConcepts)
        ? this.props.selectedConcepts
        : [];
      const includeConceptIds = existingConcepts
        .filter(item => typeof item === 'string')
        .map(item => String(item).trim())
        .filter(Boolean);
      const excludeConcepts = existingConcepts.filter(
        item => item && typeof item === 'object' && item.not
      );
      const includeConceptIdSet = new Set(includeConceptIds);

      if (includeConceptIdSet.has(targetConceptId)) {
        includeConceptIdSet.delete(targetConceptId);
      } else {
        includeConceptIdSet.add(targetConceptId);
      }

      const nextSelectedConcepts = Array.from(includeConceptIdSet).concat(
        excludeConcepts
      );
      this.props.setSelectedConcepts(nextSelectedConcepts);
      if (this.props.fetchAllData) {
        this.props.fetchAllData({
          force: true,
          refreshAlerts: true,
          scope: 'targeted'
        });
      } else {
        this.props.startTimelineFetch();
        this.props.getTimeline(false);
      }
      return;
    }

    if (type === 'photos') {
      this.setState(
        prevState => ({
          showPhotoResults: !prevState.showPhotoResults
        }),
        () =>
          localStorage.setItem(
            'resultsShowPhotoResults',
            String(this.state.showPhotoResults)
          )
      );
    }
  };

  getResultFilterMode = () => {
    const {
      showTrackResults,
      showAlertResults,
      showNoteResults,
      showPhotoResults
    } = this.state;
    const hasFilter =
      showTrackResults ||
      showAlertResults ||
      showNoteResults ||
      showPhotoResults;
    return hasFilter ? 'filtered' : 'all';
  };

  observationHasPhoto = item => {
    if (!item || !item.observationId) return false;
    const media = Array.isArray(item.media) ? item.media : [];
    return media.some(
      mediaItem =>
        mediaItem &&
        mediaItem.Media &&
        isImageMediaType(mediaItem.Media.mediaType)
    );
  };

  getInformationTypeCounts = () => {
    const toNumber = value => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const normalizeToken = value =>
      String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
    const countFromLabel = label => {
      const text = String(label || '');
      const match = text.match(/\((\d+)\)\s*$/);
      return match ? toNumber(match[1]) : 0;
    };
    const resolveNodeCount = node => {
      if (!node || typeof node !== 'object') return 0;
      const direct = toNumber(node.count);
      if (direct > 0) return direct;
      return countFromLabel(node.label);
    };
    const getNodeTokens = node => {
      if (!node || typeof node !== 'object') return [];
      return [
        node.uri,
        node.URI,
        node.id,
        node.key,
        node.value,
        node.name,
        node.label
      ]
        .map(normalizeToken)
        .filter(Boolean);
    };
    const tokenMatchesHint = (token, hint) => {
      const normalizedToken = normalizeToken(token);
      const normalizedHint = normalizeToken(hint);
      if (!normalizedToken || !normalizedHint) return false;
      return (
        normalizedToken === normalizedHint ||
        normalizedToken.endsWith(normalizedHint)
      );
    };

    const findNodeByLabel = (nodes, label) => {
      if (!Array.isArray(nodes) || nodes.length === 0) {
        return null;
      }

      const normalizedLabel = label.toLowerCase();
      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        const nodeLabel = (node.label || '').toLowerCase();
        if (
          nodeLabel === normalizedLabel ||
          nodeLabel.startsWith(`${normalizedLabel} (`)
        ) {
          return node;
        }
        const childHit = findNodeByLabel(node.children, label);
        if (childHit) {
          return childHit;
        }
      }
      return null;
    };
    const findNodeByUriHints = (nodes, hints) => {
      if (!Array.isArray(nodes) || nodes.length === 0) return null;
      const normalizedHints = (Array.isArray(hints) ? hints : [])
        .map(normalizeToken)
        .filter(Boolean);
      if (!normalizedHints.length) return null;

      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        const tokens = getNodeTokens(node);
        const matches = normalizedHints.some(hint =>
          tokens.some(token => tokenMatchesHint(token, hint))
        );
        if (matches) return node;
        const childHit = findNodeByUriHints(node.children, normalizedHints);
        if (childHit) return childHit;
      }
      return null;
    };
    const findNodeByExactUri = (nodes, expectedUri) => {
      if (!Array.isArray(nodes) || nodes.length === 0) return null;
      const normalizedExpected = String(expectedUri || '')
        .trim()
        .toLowerCase();
      if (!normalizedExpected) return null;

      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        const uri = String((node && (node.uri || node.URI || node.id)) || '')
          .trim()
          .toLowerCase();
        if (uri === normalizedExpected) return node;
        const childHit = findNodeByExactUri(node.children, normalizedExpected);
        if (childHit) return childHit;
      }
      return null;
    };

    const informationTypeNode =
      findNodeByUriHints(this.props.ontology, ['informationtype']) ||
      findNodeByLabel(this.props.ontology, 'Information type');

    if (!informationTypeNode) {
      return null;
    }

    const children = Array.isArray(informationTypeNode.children)
      ? informationTypeNode.children
      : [];

    const findChildCount = (childLabel, uriHints = []) => {
      const normalizedHints = (Array.isArray(uriHints) ? uriHints : [])
        .map(normalizeToken)
        .filter(Boolean);
      const node = children.find(child => {
        const label = (child.label || '').toLowerCase();
        const normalized = childLabel.toLowerCase();
        if (label === normalized || label.startsWith(`${normalized} (`)) {
          return true;
        }
        if (!normalizedHints.length) return false;
        const tokens = getNodeTokens(child);
        return normalizedHints.some(hint => {
          return tokens.some(token => tokenMatchesHint(token, hint));
        });
      });
      return node ? resolveNodeCount(node) : 0;
    };

    const tracksFromInformationType = findChildCount('Track', ['track']);
    const tracksNode =
      findNodeByExactUri(this.props.ontology, TRACKS_CONCEPT_URI) ||
      findNodeByUriHints(this.props.ontology, ['track']) ||
      findNodeByLabel(this.props.ontology, 'Tracks') ||
      findNodeByLabel(this.props.ontology, 'Track');
    const tracksCount = Math.max(
      tracksFromInformationType,
      tracksNode ? resolveNodeCount(tracksNode) : 0
    );
    const alerts = findChildCount('Alert', ['alert']);
    const notes = findChildCount('Note', ['note']);
    const observationsCombined = alerts + notes;

    return {
      total: resolveNodeCount(informationTypeNode),
      tracks: tracksCount,
      alerts,
      notes,
      observationsCombined
    };
  };

  normalizeFacetToken = value =>
    (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9/]+/g, '');

  getTimelineFacetTypeCounts = () => {
    const facets =
      this.props.timelineFacets && typeof this.props.timelineFacets === 'object'
        ? this.props.timelineFacets
        : null;
    if (!facets) return null;

    const toNumber = value => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const findFacetValuesByKeyHints = hints => {
      const facetKey = Object.keys(facets).find(key => {
        const normalized = this.normalizeFacetToken(key);
        return hints.some(hint => normalized.includes(hint));
      });
      if (!facetKey) return [];
      const facet = facets[facetKey];
      return Array.isArray(facet && facet.facetValues) ? facet.facetValues : [];
    };

    const trackFacetValues = findFacetValuesByKeyHints([
      'entitytype',
      'entities',
      'featuretype',
      'type',
      'informationtype'
    ]);
    const observationFacetValues = findFacetValuesByKeyHints([
      'observationclass',
      'informationtype',
      'type'
    ]);

    const sumMatching = (values, predicate) =>
      (Array.isArray(values) ? values : []).reduce((sum, item) => {
        const rawValue =
          (item && (item.value || item.name || item.label || item.id)) || '';
        const token = this.normalizeFacetToken(rawValue);
        const count = toNumber(item && item.count);
        return predicate(token) ? sum + count : sum;
      }, 0);

    const tracks = sumMatching(
      trackFacetValues,
      token => token === 'track' || token.startsWith('track/')
    );
    const alerts = sumMatching(
      observationFacetValues,
      token => token === 'alert' || token.includes('/alert')
    );
    const notes = sumMatching(
      observationFacetValues,
      token => token === 'note' || token.includes('/note')
    );

    if (tracks === 0 && alerts === 0 && notes === 0) {
      return null;
    }
    return { tracks, alerts, notes };
  };

  getResultsSource = () => {
    const { camtrapResults, timelineResults } = this.props;
    return camtrapResults && camtrapResults.length > 0
      ? camtrapResults
      : timelineResults;
  };

  getTimelineItems = timelineResults =>
    timelineResults.reduce((filtered, result) => {
      let foundObservation = result.extracted.content.find(val => {
        if (val.Observation) {
          return true;
        }
        return false;
      });

      if (foundObservation && foundObservation.Observation) {
        filtered.push(foundObservation.Observation);
      }

      let foundGeoFeature = result.extracted.content.find(val => {
        if (val.GeoFeature) {
          return true;
        }
        return false;
      });

      if (foundGeoFeature && foundGeoFeature.GeoFeature) {
        filtered.push(foundGeoFeature.GeoFeature);
      }

      return filtered;
    }, []);

  normalizeCompareValue = value =>
    (value || '')
      .toString()
      .trim()
      .toLowerCase();

  getSelectedAgentMatchValues = () => {
    const selectedAgents = Array.isArray(this.props.selectedAgents)
      ? this.props.selectedAgents
      : [];
    const values = new Set();
    selectedAgents.forEach(agent => {
      [
        agent && agent.name,
        agent && agent.value,
        agent && agent.displayLabel,
        agent && agent.id,
        agent && agent.EntityId,
        agent && agent.agentId
      ].forEach(value => {
        const normalized = this.normalizeCompareValue(value);
        if (normalized) values.add(normalized);
      });
    });
    return values;
  };

  itemMatchesSelectedAgents = item => {
    const selectedAgentValues = this.getSelectedAgentMatchValues();
    if (selectedAgentValues.size === 0) return true;
    if (!item) return false;
    const agent = item.agent || {};
    const props = item.properties || {};
    const candidates = [
      item.agentId,
      item.agentName,
      item.name,
      item.value,
      item.displayLabel,
      item.EntityId,
      item.Name,
      item.AgentName,
      item.EntityName,
      props.AgentId,
      props.agentId,
      props.AgentName,
      props.agentName,
      props.Name,
      props.EntityName,
      props.EntityId,
      agent.agentId,
      agent.agentName,
      agent.id,
      agent.name
    ];
    const normalizedCandidates = candidates
      .map(candidate => this.normalizeCompareValue(candidate))
      .filter(Boolean);
    if (!normalizedCandidates.length) return true;
    return normalizedCandidates.some(candidate =>
      selectedAgentValues.has(candidate)
    );
  };

  getItemType = item => {
    if (item.featureId) {
      return 'track';
    }

    const observationClass = (item.observationClass || '').toLowerCase();
    const observationType = (item.observationType || '').toLowerCase();

    if (observationClass.includes('alert')) {
      return 'alert';
    }

    if (observationClass.includes('note') || observationType.includes('note')) {
      return 'note';
    }

    return 'note';
  };

  getVisibleItems = items => {
    if (this.state.focusedTrackAgent) {
      return items.filter(
        item =>
          this.getItemType(item) === 'track' &&
          this.itemMatchesFocusedTrackAgent(item)
      );
    }
    const { camtrapResults } = this.props;
    if (camtrapResults && camtrapResults.length > 0) {
      // In stacked-cluster mode only show observations, not the linked
      // GeoFeature/track entries that some observation results also carry.
      // Those track entries inflate the count and can't be selected on the
      // map (the spider only shows observation markers).
      return items.filter(item => this.getItemType(item) !== 'track');
    }
    const {
      showTrackResults,
      showAlertResults,
      showNoteResults,
      showPhotoResults
    } = this.state;
    const hasTypeFilterEnabled =
      showTrackResults || showAlertResults || showNoteResults;
    return items.filter(item => {
      const itemType = this.getItemType(item);
      const matchesType =
        !hasTypeFilterEnabled ||
        (itemType === 'track' && showTrackResults) ||
        (itemType === 'alert' && showAlertResults) ||
        (itemType === 'note' && showNoteResults);
      const matchesPhoto = !showPhotoResults || this.observationHasPhoto(item);
      return matchesType && matchesPhoto;
    });
  };

  getResultTypeTotals = () => {
    const { ...props } = this.props;
    const resultTypeCounts =
      props.resultTypeCounts && typeof props.resultTypeCounts === 'object'
        ? props.resultTypeCounts
        : null;
    const querySignature = JSON.stringify({
      north: this.props.queryNorth,
      east: this.props.queryEast,
      south: this.props.querySouth,
      west: this.props.queryWest,
      start: this.props.queryStart,
      end: this.props.queryEnd,
      searchQuery: this.props.searchQuery || '',
      concepts: this.props.selectedConcepts || [],
      dataSources: this.props.selectedDataSources || [],
      agents: this.props.selectedAgents || [],
      mode: this.props.queryMode
    });
    const hasMatchingResultTypeCounts =
      resultTypeCounts &&
      resultTypeCounts.querySignature === querySignature &&
      (Number(resultTypeCounts.tracks) > 0 ||
        Number(resultTypeCounts.alerts) > 0 ||
        Number(resultTypeCounts.notes) > 0 ||
        Number(resultTypeCounts.observationsCombined) > 0);
    if (hasMatchingResultTypeCounts) {
      const treeTypeTotals = this.getInformationTypeCounts();
      const treeTracks = Math.max(
        0,
        Number(treeTypeTotals && treeTypeTotals.tracks) || 0
      );
      const treeAlerts = Math.max(
        0,
        Number(treeTypeTotals && treeTypeTotals.alerts) || 0
      );
      const treeNotes = Math.max(
        0,
        Number(treeTypeTotals && treeTypeTotals.notes) || 0
      );
      const tracks = Math.max(
        treeTracks,
        Math.max(0, Number(resultTypeCounts.tracks) || 0)
      );
      const alerts = Math.max(
        treeAlerts,
        Math.max(0, Number(resultTypeCounts.alerts) || 0)
      );
      const notes = Math.max(
        treeNotes,
        Math.max(0, Number(resultTypeCounts.notes) || 0)
      );
      const observationsCombined = Math.max(
        treeAlerts + treeNotes,
        0,
        Number(resultTypeCounts.observationsCombined) || alerts + notes
      );
      return {
        total: tracks + observationsCombined,
        tracks,
        alerts,
        notes,
        observationsCombined
      };
    }

    const typeTotals = this.getInformationTypeCounts();
    if (typeTotals) {
      const tracksCount = Number(typeTotals.tracks) || 0;
      const alertsCount = Number(typeTotals.alerts) || 0;
      const notesCount = Number(typeTotals.notes) || 0;
      const observationsCombined = Math.max(
        0,
        Number(typeTotals.observationsCombined) || alertsCount + notesCount
      );
      return {
        total:
          Number(typeTotals.total) || tracksCount + alertsCount + notesCount,
        tracks: tracksCount,
        alerts: alertsCount,
        notes: notesCount,
        observationsCombined
      };
    }

    const tracksCount = 0;
    const alertsCount = 0;
    const notesCount = 0;
    return {
      total: Number(props.timelineTotal) || 0,
      tracks: tracksCount,
      alerts: alertsCount,
      notes: notesCount,
      observationsCombined: alertsCount + notesCount
    };
  };

  itemMatchesFocusedTrackAgent = item => {
    if (!item || !this.state.focusedTrackAgent) {
      return true;
    }
    const rawMatchValues = Array.isArray(
      this.state.focusedTrackAgent.matchValues
    )
      ? this.state.focusedTrackAgent.matchValues
      : [];
    const normalizedMatchValues = new Set(
      rawMatchValues
        .map(value => this.normalizeCompareValue(value))
        .filter(Boolean)
    );
    if (!normalizedMatchValues.size) {
      return true;
    }
    const agent = item.agent || {};
    const props = item.properties || {};
    const candidates = [
      item.agentId,
      item.agentName,
      item.EntityId,
      item.Name,
      item.AgentName,
      item.EntityName,
      item.name,
      item.value,
      item.displayLabel,
      item.id,
      agent.agentId,
      agent.agentName,
      props.AgentId,
      props.agentId,
      props.AgentName,
      props.agentName,
      props.Name,
      props.name,
      props.EntityName,
      props.EntityId
    ];
    const normalizedCandidates = candidates
      .map(candidate => this.normalizeCompareValue(candidate))
      .filter(Boolean);
    if (!normalizedCandidates.length) {
      return true;
    }
    return normalizedCandidates.some(candidate =>
      normalizedMatchValues.has(candidate)
    );
  };

  getFocusedTrackFallbackItems = () => {
    if (!this.state.focusedTrackAgent) return [];
    const trackFeatures = Array.isArray(this.props.trackFeatures)
      ? this.props.trackFeatures
      : [];
    return trackFeatures
      .filter(feature => this.itemMatchesFocusedTrackAgent(feature))
      .map(feature => {
        const props = (feature && feature.properties) || {};
        return {
          featureId:
            props.EntityId || props.featureId || props.trackId || feature.id,
          featureType:
            props.featureType || props.EntityType || 'track/undefined',
          startWhen:
            props.startWhen ||
            props.StartWhen ||
            props.startDateTime ||
            props.StartDateTime,
          endWhen:
            props.endWhen ||
            props.EndWhen ||
            props.endDateTime ||
            props.EndDateTime,
          length: props.length,
          agentName:
            props.AgentName ||
            props.agentName ||
            props.Name ||
            props.EntityName,
          agent: {
            agentId: props.AgentId || props.agentId,
            agentName:
              props.AgentName ||
              props.agentName ||
              props.Name ||
              props.EntityName
          },
          geometry: feature.geometry,
          properties: props
        };
      })
      .filter(item => Boolean(item.featureId));
  };

  getFocusedTrackExpectedCount = () => {
    if (!this.state.focusedTrackAgent) return 0;

    const resultTypeTotals = this.getResultTypeTotals();
    const totalsTrackCount = Number(
      resultTypeTotals && resultTypeTotals.tracks
    );
    const timelineFacetTypeCounts = this.getTimelineFacetTypeCounts();
    const facetTrackCount = Number(
      timelineFacetTypeCounts && timelineFacetTypeCounts.tracks
    );
    const declaredExpectedTrackCount = Number(
      this.state.focusedTrackAgent.expectedTrackCount
    );
    return Math.max(
      Number.isFinite(totalsTrackCount) && totalsTrackCount > 0
        ? totalsTrackCount
        : 0,
      Number.isFinite(facetTrackCount) && facetTrackCount > 0
        ? facetTrackCount
        : 0,
      Number.isFinite(declaredExpectedTrackCount) &&
        declaredExpectedTrackCount > 0
        ? declaredExpectedTrackCount
        : 0
    );
  };

  isFocusedTrackAgentStillSelected = () => {
    const { focusedTrackAgent } = this.state;
    if (!focusedTrackAgent) return false;
    const rawMatchValues = Array.isArray(focusedTrackAgent.matchValues)
      ? focusedTrackAgent.matchValues
      : [];
    const normalizedMatchValues = new Set(
      rawMatchValues
        .map(value => this.normalizeCompareValue(value))
        .filter(Boolean)
    );
    if (!normalizedMatchValues.size) return true;
    const selectedAgents = Array.isArray(this.props.selectedAgents)
      ? this.props.selectedAgents
      : [];
    return selectedAgents.some(agent => {
      const candidates = [
        agent && agent.name,
        agent && agent.value,
        agent && agent.displayLabel,
        agent && agent.id,
        agent && agent.EntityId,
        agent && agent.agentId
      ];
      return candidates.some(candidate =>
        normalizedMatchValues.has(this.normalizeCompareValue(candidate))
      );
    });
  };

  getCurrentResultsCount = () => {
    const totals = this.getResultTypeTotals();
    const {
      showTrackResults,
      showAlertResults,
      showNoteResults,
      showPhotoResults
    } = this.state;
    if (showPhotoResults) {
      const timelineItems = this.getTimelineItems(this.getResultsSource());
      const hasTypeFilterEnabled =
        showTrackResults || showAlertResults || showNoteResults;
      const resultsCount = timelineItems.filter(item => {
        const itemType = this.getItemType(item);
        const matchesType =
          !hasTypeFilterEnabled ||
          (itemType === 'track' && showTrackResults) ||
          (itemType === 'alert' && showAlertResults) ||
          (itemType === 'note' && showNoteResults);
        return matchesType && this.observationHasPhoto(item);
      }).length;
      return { hasTypeFilterEnabled: true, resultsCount };
    }
    const hasTypeFilterEnabled =
      showTrackResults || showAlertResults || showNoteResults;
    let resultsCount = Number(this.props.timelineTotal) || 0;
    if (hasTypeFilterEnabled) {
      const showObservationOnly = showAlertResults && showNoteResults;
      resultsCount =
        (showTrackResults ? totals.tracks : 0) +
        (showObservationOnly ? Number(totals.observationsCombined) || 0 : 0) +
        (!showObservationOnly && showAlertResults ? totals.alerts : 0) +
        (!showObservationOnly && showNoteResults ? totals.notes : 0);
    }

    return { hasTypeFilterEnabled, resultsCount };
  };

  maybeFetchMoreFilteredResults = () => {
    const { showTrackResults } = this.state;
    if (this.state.focusedTrackAgent) {
      if (this.props.timelineFetching) {
        return;
      }
      const timelineItems = this.getTimelineItems(this.getResultsSource());
      const visibleItems = this.getVisibleItems(timelineItems);
      const expectedTrackCount = this.getFocusedTrackExpectedCount();
      const canLoadMoreRawResults =
        this.props.timelineResults.length < this.props.timelineTotal;
      const shouldLoadMoreForFocusedAgent =
        canLoadMoreRawResults &&
        (expectedTrackCount > 0
          ? visibleItems.length < expectedTrackCount
          : true);
      if (shouldLoadMoreForFocusedAgent) {
        if (this.props.timelineResults.length === this.lastAutoFetchLength) {
          return;
        }
        this.lastAutoFetchLength = this.props.timelineResults.length;
        if (this.props.setTransientLoading) {
          this.props.setTransientLoading(true, 'Loading all track traces...');
        }
        this.props.startTimelineFetch();
        this.props.getTimeline(true);
      }
      return;
    }
    const {
      hasTypeFilterEnabled,
      resultsCount
    } = this.getCurrentResultsCount();

    if (!hasTypeFilterEnabled || this.props.timelineFetching) {
      return;
    }

    const timelineItems = this.getTimelineItems(this.getResultsSource());
    const visibleItems = this.getVisibleItems(timelineItems);
    const pageLength = 25;
    const targetVisibleCount = Math.min(pageLength, resultsCount);

    if (targetVisibleCount <= 0) {
      return;
    }

    const canLoadMore =
      this.props.timelineResults.length <
      Math.max(this.props.timelineTotal, resultsCount);

    if (this.props.timelineResults.length === this.lastAutoFetchLength) {
      return;
    }

    if (visibleItems.length < targetVisibleCount && canLoadMore) {
      this.lastAutoFetchLength = this.props.timelineResults.length;
      if (showTrackResults && this.props.setTransientLoading) {
        this.props.setTransientLoading(true, 'Loading track details...');
      }
      this.props.startTimelineFetch();
      this.props.getTimeline(true);
    }
  };

  scrollSelectedItemIntoViewIfNeeded = () => {
    const container = this.ref.current;
    const selectedId = this.props.selected;
    if (!container || !selectedId) {
      return;
    }

    const selectedElement = document.getElementById(selectedId);
    if (!selectedElement || !container.contains(selectedElement)) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const selectedRect = selectedElement.getBoundingClientRect();
    const isFullyVisible =
      selectedRect.top >= containerRect.top &&
      selectedRect.bottom <= containerRect.bottom;

    if (!isFullyVisible) {
      selectedElement.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  };

  // Playback commands are routed through window events so results and map stay decoupled.
  dispatchTrackPlaybackCommand = (command, item = null, options = {}) => {
    const detail = {
      command
    };
    if (item && item.featureId) {
      detail.trackId = String(item.featureId);
      detail.trackItem = item;
    }
    const speedMultiplier = Number(
      options.speedMultiplier || this.state.playbackSpeedMultiplier
    );
    if (Number.isFinite(speedMultiplier) && speedMultiplier > 0) {
      detail.speedMultiplier = speedMultiplier;
    }
    window.dispatchEvent(
      new CustomEvent('focus:track-playback-control', {
        detail
      })
    );
  };

  stopTrackPlayback = syncState => {
    this.dispatchTrackPlaybackCommand('stop');
    if (syncState !== false) {
      this.setState({
        playbackTrackId: null,
        playbackRunning: false
      });
    }
  };

  handleTrackPlaybackStatus = event => {
    const detail = (event && event.detail) || {};
    const trackId = String(detail.trackId || '').trim() || null;
    const status = String(detail.status || '')
      .trim()
      .toLowerCase();
    if (status === 'playing') {
      this.setState({
        playbackTrackId: trackId,
        playbackRunning: true
      });
      return;
    }
    if (status === 'paused') {
      this.setState({
        playbackTrackId: trackId,
        playbackRunning: false
      });
      return;
    }
    if (status === 'completed' || status === 'stopped') {
      this.setState({
        playbackTrackId: null,
        playbackRunning: false
      });
    }
  };

  handleTrackPlaybackToggle = async item => {
    if (!item || !item.featureId) return;
    const trackId = String(item.featureId);
    const coordinates = this.getTrackCoordinates(item);
    if (coordinates.length < 2) return;
    if (
      this.state.playbackTrackId === trackId &&
      this.state.playbackRunning === true
    ) {
      this.dispatchTrackPlaybackCommand('pause', item, {
        speedMultiplier: this.state.playbackSpeedMultiplier
      });
      this.setState({ playbackRunning: false });
      return;
    }
    if (this.state.playbackTrackId !== trackId) {
      try {
        await this.props.getTrackObservations(item.featureId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Loading linked observations for playback failed', error);
      }
    }
    this.dispatchTrackPlaybackCommand('play', item, {
      speedMultiplier: this.state.playbackSpeedMultiplier
    });
    this.setState({
      playbackTrackId: trackId,
      playbackRunning: true
    });
  };

  handleTrackPlaybackSpeedChange = (speedMultiplier, item = null) => {
    const normalizedMultiplier = Number(speedMultiplier);
    if (!Number.isFinite(normalizedMultiplier) || normalizedMultiplier <= 0) {
      return;
    }
    this.setState({
      playbackSpeedMultiplier: normalizedMultiplier
    });
    this.dispatchTrackPlaybackCommand('speed', item, {
      speedMultiplier: normalizedMultiplier
    });
  };

  getEntireTrack = async id => {
    const trackId = String(id);
    let query = new QueryBuilder.SearchQuery().withQueryText(
      'entityId:"' + id + '"'
    );
    const knownTrackObservationCount = Object.prototype.hasOwnProperty.call(
      this.props.trackObservationCounts || {},
      trackId
    )
      ? Math.max(0, Number(this.props.trackObservationCounts[trackId]) || 0)
      : null;
    const shouldLoadLinkedObservations = knownTrackObservationCount !== 0;

    const withTimeout = (promise, timeoutMs) => {
      let timerId = null;
      const timeoutPromise = new Promise((_, reject) => {
        timerId = setTimeout(() => {
          reject(new Error('Track loading timed out'));
        }, timeoutMs);
      });
      return Promise.race([Promise.resolve(promise), timeoutPromise]).finally(
        () => {
          if (timerId) clearTimeout(timerId);
        }
      );
    };

    this.setState({
      trackLoading: true,
      linkedObservationsLoadingTrackId: shouldLoadLinkedObservations
        ? trackId
        : null
    });
    if (this.props.setTransientLoading) {
      this.props.setTransientLoading(true, 'Loading all track traces...');
    }
    try {
      const requests = [
        withTimeout(this.props.getEntireTrack('track', '0', query), 25000),
        withTimeout(this.props.getTrackInfo(id), 25000)
      ];
      if (shouldLoadLinkedObservations) {
        requests.push(withTimeout(this.props.getTrackObservations(id), 25000));
      }
      await Promise.allSettled(requests);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Loading selected track failed/timed out', error);
    } finally {
      if (this.props.setTransientLoading) {
        this.props.setTransientLoading(false, '');
      }
      this.setState({
        trackLoading: false,
        linkedObservationsLoadingTrackId: null
      });
    }
  };

  handleTrackItemSelection = async item => {
    if (!item || !item.featureId) return;
    this.stopTrackPlayback();
    const trackId = String(item.featureId);
    if (this.state.focusedTrackAgent) {
      this.props.setSelectedItem(item);
      this.setState({
        activeTrackListContext: {
          trackId,
          trackItem: item,
          observations: []
        }
      });
      try {
        await this.props.getTrackObservations(item.featureId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Loading track observations failed', error);
      }
      const coordinates = this.getTrackCoordinates(item);
      if (coordinates.length > 0) {
        window.dispatchEvent(
          new CustomEvent('focus:zoom-to-track-item-bounds', {
            detail: { coordinates }
          })
        );
      }
      return;
    }
    this.props.setSelectedItem(item);
    this.setState({
      activeTrackListContext: {
        trackId,
        trackItem: item,
        observations: []
      }
    });
    this.getEntireTrack(item.featureId);
  };

  handleTrackObservationBadgeClick = async item => {
    if (!item || !item.featureId) return;
    this.stopTrackPlayback();
    this.props.setSelectedItem(item);
    this.setState({
      activeTrackListContext: {
        trackId: String(item.featureId),
        trackItem: item,
        observations: []
      }
    });
    this.getEntireTrack(item.featureId);
  };

  closeSelectedTrackView = () => {
    this.stopTrackPlayback();
    const lastTrackId = this.state.activeTrackListContext?.trackId;
    this.setState({ activeTrackListContext: null }, () => {
      if (lastTrackId) {
        const el = document.getElementById(lastTrackId);
        if (el) {
          el.scrollIntoView({ block: 'center' });
        }
      }
    });
    this.props.clearSelectedItem();
  };

  getObservationTrackId = item => {
    if (!item || !item.observationId) return null;
    const props = item.properties || {};
    const trackRef = item.track || {};
    const candidates = [
      item.trackId,
      item.TrackId,
      item.trackFeatureId,
      item.TrackFeatureId,
      item.parentTrackId,
      item.parentFeatureId,
      item.trackRef,
      item.TrackRef,
      props.trackId,
      props.TrackId,
      props.trackFeatureId,
      props.TrackFeatureId,
      props.parentTrackId,
      props.parentFeatureId,
      props.trackRef,
      props.TrackRef,
      trackRef.featureId,
      trackRef.EntityId,
      trackRef.trackId
    ];
    const found = candidates.find(
      value =>
        value !== undefined && value !== null && String(value).trim() !== ''
    );
    return found ? String(found) : null;
  };

  getTrackObservationCountInfo = () => {
    const countsMap = this.props.trackObservationCounts || {};
    const trackCounts = new Map(
      Object.keys(countsMap).map(trackId => [
        String(trackId),
        Math.max(0, Number(countsMap[trackId]) || 0)
      ])
    );
    return {
      trackCounts
    };
  };

  ensureVisibleTrackObservationCounts = displayItems => {
    const countMap = this.props.trackObservationCounts || {};
    const visibleTrackIds = (displayItems || [])
      .filter(item => item && item.featureId)
      .map(item => String(item.featureId))
      .filter(Boolean);
    visibleTrackIds.forEach(trackId => {
      if (
        Object.prototype.hasOwnProperty.call(countMap, trackId) ||
        this.pendingTrackCountRequests.has(trackId)
      ) {
        return;
      }
      this.pendingTrackCountRequests.add(trackId);
      Promise.resolve(this.props.getTrackObservationsCount(trackId))
        .catch(() => null)
        .finally(() => {
          this.pendingTrackCountRequests.delete(trackId);
        });
    });
  };

  getTrackCoordinates = item => {
    const coordinates = [];
    const pushCoord = coord => {
      if (!Array.isArray(coord) || coord.length < 2) return;
      const lng = Number(coord[0]);
      const lat = Number(coord[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      coordinates.push([lng, lat]);
    };
    const pushGeometry = geometry => {
      if (!geometry || !geometry.type || !geometry.coordinates) return;
      if (geometry.type === 'Point') {
        pushCoord(geometry.coordinates);
        return;
      }
      if (geometry.type === 'LineString') {
        geometry.coordinates.forEach(pushCoord);
        return;
      }
      if (geometry.type === 'MultiLineString') {
        geometry.coordinates.forEach(line => line.forEach(pushCoord));
      }
    };
    pushGeometry(item && item.geometry);
    if (Array.isArray(item && item.where)) {
      pushCoord(item.where);
    }
    if (item && item.startWhere && Array.isArray(item.startWhere.coordinates)) {
      pushCoord(item.startWhere.coordinates);
    }
    if (item && item.endWhere && Array.isArray(item.endWhere.coordinates)) {
      pushCoord(item.endWhere.coordinates);
    }
    return coordinates;
  };

  getDisplayItems = visibleItems => {
    const itemsByKey = new Map();
    const normalizeId = value => String(value || '').trim();
    const buildTrackSignatureKey = item => {
      const props = (item && item.properties) || {};
      const featureType =
        item.featureType || props.featureType || props.EntityType || '';
      const startWhen =
        item.startWhen || props.startWhen || props.StartWhen || '';
      const endWhen = item.endWhen || props.endWhen || props.EndWhen || '';
      const agentName =
        item.agentName ||
        (item.agent && item.agent.agentName) ||
        props.AgentName ||
        props.agentName ||
        '';
      const length =
        item.length !== undefined && item.length !== null
          ? String(item.length)
          : '';
      return `track-signature-${normalizeId(featureType)}-${normalizeId(
        startWhen
      )}-${normalizeId(endWhen)}-${normalizeId(agentName)}-${normalizeId(
        length
      )}`;
    };
    const pushItem = item => {
      if (!item) return;
      const props = (item && item.properties) || {};
      const trackId = normalizeId(
        item.featureId ||
          item.EntityId ||
          item.trackId ||
          props.EntityId ||
          props.featureId ||
          props.trackId
      );
      const observationId = normalizeId(
        item.observationId ||
          item.ObservationId ||
          item.EntityId ||
          props.observationId ||
          props.ObservationId ||
          props.EntityId
      );
      const key = trackId
        ? `track-${trackId}`
        : observationId
        ? `obs-${observationId}`
        : this.getItemType(item) === 'track'
        ? buildTrackSignatureKey(item)
        : null;
      if (!key || itemsByKey.has(key)) return;
      itemsByKey.set(key, item);
    };

    if (this.state.focusedTrackAgent) {
      (visibleItems || []).forEach(pushItem);
      this.getFocusedTrackFallbackItems().forEach(pushItem);
      return Array.from(itemsByKey.values()).sort(this.sortItemsByDateDesc);
    }

    const activeTrackContext = this.state.activeTrackListContext;
    if (activeTrackContext && activeTrackContext.trackId) {
      const allTimelineItems = this.getTimelineItems(
        this.props.timelineResults
      );
      const contextTrack =
        allTimelineItems.find(
          item =>
            item &&
            item.featureId &&
            String(item.featureId) === String(activeTrackContext.trackId)
        ) || activeTrackContext.trackItem;
      pushItem(contextTrack);
      const contextObservations = Array.isArray(activeTrackContext.observations)
        ? activeTrackContext.observations
        : [];
      contextObservations.sort(this.sortItemsByDateDesc).forEach(pushItem);
      return Array.from(itemsByKey.values());
    }

    (visibleItems || []).forEach(pushItem);
    const { camtrapResults } = this.props;
    const isCamtrapMode = camtrapResults && camtrapResults.length > 0;
    if (!isCamtrapMode) {
      const selectedObservationId = this.getSelectedObservationId(
        this.props.timelineSelectedItem
      );
      if (selectedObservationId) {
        pushItem({
          ...this.props.timelineSelectedItem,
          observationId: selectedObservationId
        });
      }
    }
    return Array.from(itemsByKey.values()).sort(this.sortItemsByDateDesc);
  };

  getItemTimestamp = item => {
    if (!item) return 0;
    const candidateDates = [
      item.when,
      item.startWhen,
      item.endWhen,
      item.LastSeenDateTime,
      item.lastSeenDateTime
    ];
    for (let index = 0; index < candidateDates.length; index += 1) {
      const value = candidateDates[index];
      if (!value) continue;
      const timestamp = Date.parse(value);
      if (!Number.isNaN(timestamp)) {
        return timestamp;
      }
    }
    return 0;
  };

  sortItemsByDateDesc = (left, right) => {
    const leftTime = this.getItemTimestamp(left);
    const rightTime = this.getItemTimestamp(right);
    return rightTime - leftTime;
  };

  getListPagingState = visibleItems => {
    if (this.state.focusedTrackAgent) {
      return {
        canLoadMoreRawResults: false,
        needsMoreForActiveView: false,
        canLoadMore: false
      };
    }
    if (
      this.props.timelineSelectedItem &&
      this.props.timelineSelectedItem.featureId
    ) {
      return {
        canLoadMoreRawResults: false,
        needsMoreForActiveView: false,
        canLoadMore: false
      };
    }
    const {
      hasTypeFilterEnabled,
      resultsCount
    } = this.getCurrentResultsCount();
    const visibleCount = (visibleItems || []).length;
    const canLoadMoreRawResults =
      this.props.timelineResults.length < this.props.timelineTotal;
    const needsMoreForActiveView = hasTypeFilterEnabled
      ? visibleCount < resultsCount
      : canLoadMoreRawResults;
    const canLoadMore = canLoadMoreRawResults && needsMoreForActiveView;
    return {
      canLoadMoreRawResults,
      needsMoreForActiveView,
      canLoadMore
    };
  };

  hasReachedFilteredLimit = visibleItems => {
    const {
      hasTypeFilterEnabled,
      resultsCount
    } = this.getCurrentResultsCount();
    if (!hasTypeFilterEnabled) return false;
    const visibleCount = (visibleItems || []).length;
    return resultsCount > 0 && visibleCount >= resultsCount;
  };

  render() {
    const { ...props } = this.props;
    const timelineItems = this.getTimelineItems(this.getResultsSource());
    const visibleItems = this.getVisibleItems(timelineItems);
    const displayItems = this.getDisplayItems(visibleItems);
    const trackObservationCountInfo = this.getTrackObservationCountInfo();
    const selectedTrackObservationCount = Array.isArray(
      this.props.timelineSelectedTrackObservations
    )
      ? this.props.timelineSelectedTrackObservations.length
      : 0;
    const activeTrackObservationIds = new Set(
      (
        (this.state.activeTrackListContext &&
          this.state.activeTrackListContext.observations) ||
        []
      )
        .map(item => item && item.observationId)
        .filter(Boolean)
        .map(id => String(id))
    );
    const isLoading = props.timelineFetching;
    const { canLoadMore } = this.getListPagingState(visibleItems);
    const reachedFilteredLimit = this.hasReachedFilteredLimit(visibleItems);
    const showLoadMoreHint = canLoadMore && !reachedFilteredLimit;
    const currentLoadingStep = String(
      (props.dataLoading && props.dataLoading.current) || ''
    ).trim();
    const isTrackScopedLoading =
      currentLoadingStep === 'Loading tracks...' ||
      currentLoadingStep === 'Loading track details...' ||
      currentLoadingStep === 'Loading all track traces...' ||
      currentLoadingStep === 'Loading track traces...' ||
      currentLoadingStep === 'Loading selected track traces...' ||
      currentLoadingStep === 'Zoom in on track bounds';
    const listLoading =
      isLoading ||
      this.state.trackLoading ||
      (props.dataLoading && props.dataLoading.inFlight && isTrackScopedLoading);
    const focusedTrackExpectedCount = this.state.focusedTrackAgent
      ? this.getFocusedTrackExpectedCount()
      : 0;
    const showPaginationLoading =
      isLoading &&
      canLoadMore &&
      !this.state.focusedTrackAgent &&
      !this.state.trackLoading &&
      !isTrackScopedLoading;
    const hasSelectedTrack =
      !!this.state.activeTrackListContext && !this.state.focusedTrackAgent;
    const showFocusedTrackPaginationLoading =
      displayItems.length > 0 &&
      !hasSelectedTrack &&
      this.state.focusedTrackAgent &&
      this.state.focusedTrackLoadingActive &&
      (isLoading || isTrackScopedLoading);
    const focusedTrackAgentLabel =
      this.state.focusedTrackAgent &&
      this.state.focusedTrackAgent.agentName &&
      this.state.focusedTrackAgent.agentName.trim() !== ''
        ? this.state.focusedTrackAgent.agentName
        : 'selected agent';
    const focusedTrackCount = this.state.focusedTrackAgent
      ? Math.max(focusedTrackExpectedCount, displayItems.length)
      : 0;
    const { resultsCount } = this.getCurrentResultsCount();
    const { camtrapResults } = this.props;
    const isCamtrapFocus = camtrapResults && camtrapResults.length > 0;
    const camtrapCount = isCamtrapFocus ? displayItems.length : 0;

    return (
      <div className="h-100 timeline-loading-host">
        <div className="card border-bottom-0 rounded-0">
          <div className="card-header pb-2 pr-3 mr-0">
            <h6 className="pt-1 pb-0 results-header-title">
              {isCamtrapFocus ? (
                <>
                  <span className="results-title">
                    {`Stacked results (${camtrapCount})`}
                  </span>
                  <span className="results-summary">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={this.props.clearCamtrapFocus}
                    >
                      Close stacked cluster
                    </button>
                  </span>
                </>
              ) : this.state.focusedTrackAgent ? (
                <>
                  <span className="results-title">
                    {focusedTrackCount > 0
                      ? `All ${focusedTrackCount} tracks of ${focusedTrackAgentLabel}`
                      : `All tracks of ${focusedTrackAgentLabel}`}
                  </span>
                  <span className="results-summary">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={this.closeFocusedTracksView}
                    >
                      Close
                    </button>
                  </span>
                </>
              ) : hasSelectedTrack ? (
                <span className="selected-track-header">
                  <span className="selected-track-actions">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={this.closeSelectedTrackView}
                    >
                      Close track
                    </button>
                  </span>
                </span>
              ) : (
                <>
                  <span className="results-title">
                    {`Results (${Math.max(0, Number(resultsCount) || 0)})`}
                  </span>
                  <span className="results-summary">
                    <button
                      type="button"
                      className={`results-summary-item results-count-toggle${
                        this.state.showNoteResults ? ' active' : ''
                      }`}
                      title="Show notes"
                      aria-pressed={this.state.showNoteResults}
                      onClick={() => this.toggleResultType('notes')}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M2 1.5A1.5 1.5 0 013.5 0h9A1.5 1.5 0 0114 1.5v13a.5.5 0 01-.854.354L10.293 12H3.5A1.5 1.5 0 012 10.5v-9z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className={`results-summary-item results-count-toggle alert-toggle${
                        this.state.showAlertResults ? ' active' : ''
                      }`}
                      title="Show alerts"
                      aria-pressed={this.state.showAlertResults}
                      onClick={() => this.toggleResultType('alerts')}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M8 16a2 2 0 001.995-1.85L10 14H6a2 2 0 001.85 1.995L8 16zm.104-14.996a3 3 0 00-2.995 2.824L5.104 4v1.018C5.104 6.31 4.6 7.555 3.736 8.508l-.197.206A1 1 0 003.104 9.5V11h9.792V9.5a1 1 0 00-.285-.7l-.197-.206c-.864-.953-1.368-2.198-1.368-3.49V4a3 3 0 00-2.942-2.998z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className={`results-summary-item results-count-toggle track-toggle${
                        this.state.showTrackResults ? ' active' : ''
                      }`}
                      title="Show tracks"
                      aria-pressed={this.state.showTrackResults}
                      onClick={() => this.toggleResultType('tracks')}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="2.25"
                          cy="12.5"
                          r="1.75"
                          fill="currentColor"
                        />
                        <circle
                          cx="13.75"
                          cy="3.5"
                          r="1.75"
                          fill="currentColor"
                        />
                        <path
                          d="M4.25 11.75L8.1 7.9C8.65 7.35 9.35 7 10.1 6.9L11.7 6.7C12.6 6.58 13.3 5.87 13.4 4.96L13.5 4"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className={`results-summary-item results-count-toggle photo-toggle${
                        this.state.showPhotoResults ? ' active' : ''
                      }`}
                      title="Show observations with photos"
                      aria-pressed={this.state.showPhotoResults}
                      onClick={() => this.toggleResultType('photos')}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.5 7.5H8L9.8 5.5H14.2L16 7.5H19.5C20.6 7.5 21.5 8.4 21.5 9.5V17.5C21.5 18.6 20.6 19.5 19.5 19.5H4.5C3.4 19.5 2.5 18.6 2.5 17.5V9.5C2.5 8.4 3.4 7.5 4.5 7.5Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="13.2"
                          r="3.2"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                      </svg>
                    </button>
                  </span>
                </>
              )}
            </h6>
            <span
              type="button"
              className="btn btn-outline-info btn-sm float-right d-none "
              data-toggle="collapse"
              href="#multiCollapseExample2"
              role="button"
              aria-expanded="false"
              aria-controls="multiCollapseExample1"
            >
              CSV
              <svg
                className="bi bi-download ml-1"
                width="1em"
                height="1em"
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M.5 8a.5.5 0 01.5.5V12a1 1 0 001 1h12a1 1 0 001-1V8.5a.5.5 0 011 0V12a2 2 0 01-2 2H2a2 2 0 01-2-2V8.5A.5.5 0 01.5 8z"
                  clipRule="evenodd"
                />
                <path
                  fillRule="evenodd"
                  d="M5 7.5a.5.5 0 01.707 0L8 9.793 10.293 7.5a.5.5 0 11.707.707l-2.646 2.647a.5.5 0 01-.708 0L5 8.207A.5.5 0 015 7.5z"
                  clipRule="evenodd"
                />
                <path
                  fillRule="evenodd"
                  d="M8 1a.5.5 0 01.5.5v8a.5.5 0 01-1 0v-8A.5.5 0 018 1z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </div>
        </div>
        <div
          ref={this.ref}
          className={
            this.props.resultsCollapsed
              ? 'list-container no-scroll'
              : 'list-container'
          }
        >
          <div>
            {displayItems.map((item, key) => {
              const observationId = this.getSelectedObservationId(item);
              const isLinkedToSelectedTrack =
                !!observationId &&
                (activeTrackObservationIds.has(String(observationId)) ||
                  (!!this.props.timelineSelectedItem.featureId &&
                    this.props.timelineSelectedTrackObservations.some(
                      entry =>
                        String(
                          (entry && entry.Observation
                            ? entry.Observation.observationId
                            : entry && entry.observationId) || ''
                        ) === String(observationId)
                    )));
              if (this.isObservationItem(item))
                return (
                  <ObservationItem
                    key={key}
                    item={{
                      ...item,
                      observationId
                    }}
                    selected={
                      String(this.props.selected || '') ===
                      String(observationId || '')
                        ? true
                        : false
                    }
                    viewed={!!this.props.viewedObservationIds[observationId]}
                    setSelectedItem={
                      isCamtrapFocus ? () => {} : props.setSelectedItem
                    }
                    openImage={props.openImageModal}
                    openObservation={this.openObservationDetails}
                    onOpenMedia={this.openObservationMedia}
                    linkedToSelectedTrack={isLinkedToSelectedTrack}
                  />
                );
              else if (item.featureId)
                return (
                  <React.Fragment key={key}>
                    <TrackItem
                      item={item}
                      trackObservationCount={
                        this.props.timelineSelectedItem.featureId &&
                        String(this.props.timelineSelectedItem.featureId) ===
                          String(item.featureId)
                          ? trackObservationCountInfo.trackCounts.get(
                              String(item.featureId)
                            ) ?? selectedTrackObservationCount
                          : trackObservationCountInfo.trackCounts.has(
                              String(item.featureId)
                            )
                          ? trackObservationCountInfo.trackCounts.get(
                              String(item.featureId)
                            )
                          : null
                      }
                      trackColor={
                        this.state.focusedTrackAgent
                          ? getTrackColor(item.featureId)
                          : null
                      }
                      selected={
                        String(this.props.selected || '') ===
                        String(item.featureId || '')
                          ? true
                          : false
                      }
                      viewed={!!this.props.viewedTrackIds[item.featureId]}
                      setSelectedItem={() =>
                        this.handleTrackItemSelection(item)
                      }
                      onObservationBadgeClick={() =>
                        this.handleTrackObservationBadgeClick(item)
                      }
                      isPlaybackActive={
                        this.state.playbackTrackId === String(item.featureId) &&
                        this.state.playbackRunning
                      }
                      playbackDisabled={
                        this.getTrackCoordinates(item).length < 2
                      }
                      onTogglePlayback={() =>
                        this.handleTrackPlaybackToggle(item)
                      }
                      playbackSpeedMultiplier={
                        this.state.playbackSpeedMultiplier
                      }
                      onPlaybackSpeedChange={speed =>
                        this.handleTrackPlaybackSpeedChange(speed, item)
                      }
                      openTrack={props.openTrackModal}
                    />
                    {this.props.timelineSelectedItem.featureId &&
                      String(this.props.timelineSelectedItem.featureId) ===
                        String(item.featureId) &&
                      this.state.linkedObservationsLoadingTrackId ===
                        String(item.featureId) && (
                        <div className="track-observations-loading">
                          <span
                            className="results-loading-spinner"
                            aria-hidden="true"
                          />
                          <span>Loading linked observations...</span>
                        </div>
                      )}
                  </React.Fragment>
                );
              return null;
            })}
            {displayItems.length === 0 && listLoading && (
              <div className="no-results-container">
                <span className="no-results-container-text">Loading...</span>
              </div>
            )}
            {displayItems.length === 0 && !listLoading && (
              <div className="no-results-container">
                <span className="no-results-container-text">
                  There are no tracks, observations or agents within the current
                  selection. Zoom out on the map or change the filter and time
                  settings.
                </span>
              </div>
            )}
            {displayItems.length > 0 &&
              !isLoading &&
              !hasSelectedTrack &&
              !this.state.focusedTrackAgent &&
              !this.state.focusedTrackLoadingActive && (
                <div
                  className={`results-scroll-footer${
                    showLoadMoreHint ? ' clickable' : ''
                  }`}
                  role={showLoadMoreHint ? 'button' : undefined}
                  tabIndex={showLoadMoreHint ? 0 : -1}
                  onClick={
                    showLoadMoreHint ? this.handleLoadMoreClick : undefined
                  }
                  onKeyDown={
                    showLoadMoreHint
                      ? e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this.handleLoadMoreClick();
                          }
                        }
                      : undefined
                  }
                >
                  {showLoadMoreHint
                    ? 'Scroll or click here to load more'
                    : 'End of list'}
                </div>
              )}
            {displayItems.length > 0 &&
              showPaginationLoading &&
              !hasSelectedTrack && (
                <div className="results-loading-footer" role="status">
                  <span
                    className="results-loading-spinner"
                    aria-hidden="true"
                  />
                  <span>Loading more results...</span>
                </div>
              )}
            {showFocusedTrackPaginationLoading && (
              <div className="results-loading-footer" role="status">
                <span className="results-loading-spinner" aria-hidden="true" />
                <span>Loading more results...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const getTimelineResults = state => state.timeline.results || [];
const getTimelineTotal = state => state.timeline.total || 0;
const getTimelineFetching = state => state.timeline.fetchingList || false;
const getTimelineFacets = state => state.timeline.facets || {};
const getScrollToTop = state => state.timeline.scrollToTop || false;
const getTimelineSelectedItem = state => state.timeline.selectedItem || {};
const getTimelineSelectedTrackObservations = state =>
  state.timeline.selectedTrackObservations || [];
const getTrackObservationCounts = state =>
  state.timeline.trackObservationCounts || {};
const getViewedObservationIds = state =>
  state.timeline.viewedObservationIds || {};
const getViewedTrackIds = state => state.timeline.viewedTrackIds || {};
const getOntology = state => state.ontology.ontology || [];
const getSelectedAgents = state => state.query.agents || [];
const getSelectedConcepts = state => state.query.concepts || [];
const getSelectedDataSources = state => state.query.dataSources || [];
const getQueryMode = state => state.query.mode;
const getSearchQuery = state => state.query.searchQuery || '';
const getTrackLayerFeatures = state => {
  const trackLayer = state.layer.trackLayer || {};
  return Array.isArray(trackLayer.features) ? trackLayer.features : [];
};
const getResultTypeCounts = state => state.timeline.resultTypeCounts || null;
const getQueryNorth = state => state.query.north;
const getQueryEast = state => state.query.east;
const getQuerySouth = state => state.query.south;
const getQueryWest = state => state.query.west;
const getQueryStart = state => state.query.start;
const getQueryEnd = state => state.query.end;
const getCamtrapResults = state => state.layer.camtrapResults || [];

const mapStateToProps = createSelector(
  getTimelineResults,
  getTimelineTotal,
  getTimelineFetching,
  getTimelineFacets,
  getScrollToTop,
  getTimelineSelectedItem,
  getTimelineSelectedTrackObservations,
  getTrackObservationCounts,
  getViewedObservationIds,
  getViewedTrackIds,
  getOntology,
  getSelectedAgents,
  getSelectedConcepts,
  getSelectedDataSources,
  getQueryMode,
  getSearchQuery,
  getTrackLayerFeatures,
  getResultTypeCounts,
  getQueryNorth,
  getQueryEast,
  getQuerySouth,
  getQueryWest,
  getQueryStart,
  getQueryEnd,
  getCamtrapResults,
  (
    timelineResults,
    timelineTotal,
    timelineFetching,
    timelineFacets,
    scrollToTop,
    timelineSelectedItem,
    timelineSelectedTrackObservations,
    trackObservationCounts,
    viewedObservationIds,
    viewedTrackIds,
    ontology,
    selectedAgents,
    selectedConcepts,
    selectedDataSources,
    queryMode,
    searchQuery,
    trackFeatures,
    resultTypeCounts,
    queryNorth,
    queryEast,
    querySouth,
    queryWest,
    queryStart,
    queryEnd,
    camtrapResults
  ) => ({
    timelineResults,
    timelineTotal,
    timelineFetching,
    timelineFacets,
    scrollToTop,
    ontology,
    timelineSelectedItem,
    selected:
      timelineSelectedItem.observationId ||
      timelineSelectedItem.ObservationId ||
      timelineSelectedItem.EntityId ||
      (timelineSelectedItem.properties &&
        timelineSelectedItem.properties.EntityId) ||
      timelineSelectedItem.featureId ||
      null,
    timelineSelectedTrackObservations,
    trackObservationCounts,
    viewedObservationIds,
    viewedTrackIds,
    selectedAgents,
    selectedConcepts,
    selectedDataSources,
    queryMode,
    searchQuery,
    trackFeatures,
    resultTypeCounts,
    queryNorth,
    queryEast,
    querySouth,
    queryWest,
    queryStart,
    queryEnd,
    camtrapResults
  })
);

const mapDispatchToProps = {
  setSelectedItem: actions.timeline.setSelectedItem,
  getEntireTrack: actions.layer.getEntireTrack,
  getTrackInfo: actions.layer.getTrackInfo,
  getTrackObservations: actions.layer.getTrackObservations,
  getTrackObservationsCount: actions.layer.getTrackObservationsCount,
  openImageModal: actions.modals.openImageModal,
  openObservationModal: actions.modals.openObservationModal,
  openTrackModal: actions.modals.openTrackModal,
  getSingleItem: actions.timeline.getSingleItem,
  getTimeline: actions.timeline.getTimeline,
  startTimelineFetch: actions.timeline.startTimelineFetch,
  setSelectedConcepts: actions.query.setSelectedConcepts,
  setSelectedAgents: actions.query.setSelectedAgents,
  clearEntireTrack: actions.layer.clearEntireTrack,
  clearSelectedItem: actions.timeline.clearSelectedItem,
  getTracksForCurrentFilters: actions.user.getTracksForCurrentFilters,
  setSelectedLayers: actions.layer.setSelectedLayers,
  clearCamtrapFocus: actions.layer.clearCamtrapFocus
};

export default connect(mapStateToProps, mapDispatchToProps)(ObservationList);
